import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { useBoolean } from 'hooks';
import { reverseFormTime } from 'utils/datetime';

import { mapHoldDownsToRows } from './utils';
import { crewMemberDetailsStore as store } from '../../../crewMemberDetailsStore';

const EMPTY_HOLD_DOWNS = [];

export const useHoldDowns = () => {
	const [selectedHoldDownId, setSelectedHoldDownId] = useState(null);
	const active = useActiveElements();
	const navigate = useNavigate();
	const [
		showDeleteReplacement,
		{ setToTrue: openDeleteReplacement, setToFalse: closeDeleteReplacement },
	] = useBoolean();

	const { employeeInfo, currentHomeBase } = store;
	const holdDowns = employeeInfo?.holdDowns ?? EMPTY_HOLD_DOWNS;

	const holdDownRows = useMemo(
		() => mapHoldDownsToRows(holdDowns, employeeInfo),
		[holdDowns, employeeInfo]
	);

	const hasHoldDown = holdDownRows.length > 0;

	const handleAddClick = useCallback(() => {
		active.setActiveElement(MODAL_NAMES.holdDown, {});
	}, [active]);

	const handleDeleteClick = useCallback(
		(holdDownId) => {
			setSelectedHoldDownId(holdDownId);
			openDeleteReplacement();
		},
		[openDeleteReplacement]
	);

	const handleCloseDeleteReplacement = useCallback(() => {
		closeDeleteReplacement();
		setSelectedHoldDownId(null);
	}, [closeDeleteReplacement]);

	const handleEditClick = useCallback(
		(holdDownId) => {
			const holdDown = holdDowns.find(({ id }) => id === holdDownId);

			if (!holdDown) return;

			const time = reverseFormTime(
				holdDown.effectiveTimestamp,
				holdDown.expireTimestamp,
				currentHomeBase?.city?.timezone
			);

			active.setActiveElement(MODAL_NAMES.holdDown, {
				initialData: {
					id: holdDown.id,
					...time,
					crewMemberId: holdDown.crewMember.id,
					originalCrewMemberName: holdDown.originalCrewMember.fullName,
					originalCrewMemberId: holdDown.originalCrewMember.id,
					note: holdDown.note ?? '',
				},
			});
		},
		[active, holdDowns, currentHomeBase?.city?.timezone]
	);

	const handleShowWorkAssignmentsClick = useCallback(
		(holdDownId) => {
			const holdDown = holdDowns.find(({ id }) => id === holdDownId);

			if (!holdDown) return;

			const { originalCrewMember, crewMember, effectiveTimestamp, expireTimestamp } = holdDown;
			const isReplacedHoldDown = holdDown.originalCrewMember?.id === employeeInfo?.id;
			const params = new URLSearchParams({
				assignee: isReplacedHoldDown ? crewMember.fullName : originalCrewMember.fullName,
				fromDate: effectiveTimestamp,
				toDate: expireTimestamp,
			});

			navigate(`/work-assignments?${params.toString()}`);
		},
		[employeeInfo?.id, holdDowns, navigate]
	);

	return {
		employeeInfo,
		holdDownRows,
		selectedHoldDownId,
		showDeleteReplacement,
		hasHoldDown,
		handleAddClick,
		handleDeleteClick,
		handleCloseDeleteReplacement,
		handleEditClick,
		handleShowWorkAssignmentsClick,
	};
};
